import { Controller, type Control } from 'react-hook-form';
import { Button, ButtonLink, Form, FormSection, Input, Label, PageHeader } from '@/shared/components';
import { TemplateOwnerField } from '../components/TemplateOwnerField';
import { TemplateMetadataFields } from '../components/TemplateMetadataFields';
import { useCreateTemplateForm } from '../hooks/useCreateTemplateForm';
import type { CreateTemplateFormValues, TemplateMetadataFormValues } from '../schemas/documentTemplate.schema';

const KEY_HINT = 'Filled in from the name. Lowercase letters, digits and underscores; cannot be changed later.';

function KeyField({ control }: { control: Control<CreateTemplateFormValues> }) {
  return (
    <div className="sm:col-span-2">
      <Label htmlFor="template-key">Key</Label>
      <Controller
        name="key"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            id="template-key"
            className="font-mono"
            placeholder="welcome_letter"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={!!error}
            hint={error?.message ?? KEY_HINT}
            hintId="template-key-hint"
            aria-invalid={!!error}
            aria-describedby="template-key-hint"
          />
        )}
      />
    </div>
  );
}

export function DocumentTemplateCreate() {
  const { form, submit, isSaving, isInternal, hasDealers, canSubmit } = useCreateTemplateForm();

  return (
    <div className="space-y-8">
      <PageHeader
        title="New template"
        subtitle="You write the content next, in the draft editor. Nothing reaches customers until you publish."
      />

      <Form className="space-y-8" onSubmit={submit}>
        <FormSection title="Details" description="How the template is found and who owns it.">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <TemplateOwnerField
                control={form.control}
                setValue={form.setValue}
                isInternal={isInternal}
                hasDealers={hasDealers}
              />
            </div>
            <TemplateMetadataFields
              control={form.control as unknown as Control<TemplateMetadataFormValues>}
              afterName={<KeyField control={form.control} />}
            />
          </div>
        </FormSection>

        <div className="flex justify-end gap-3 border-t border-mp-border pt-6">
          <ButtonLink to="/admin/document-templates" variant="neutral" appearance="ghost" size="sm">
            Cancel
          </ButtonLink>
          <Button type="submit" size="sm" disabled={isSaving || !canSubmit}>
            {isSaving ? 'Creating...' : 'Create and write content'}
          </Button>
        </div>
      </Form>
    </div>
  );
}
